import { Screen, useNavigate } from "flemo";
import {
  BellIcon,
  BellRingIcon,
  BookOpenIcon,
  CheckIcon,
  ChevronRightIcon,
  ClipboardPenIcon,
  LoaderCircleIcon,
  SparklesIcon,
  TargetIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { getNotificationSettings, upsertNotificationSettings } from "@/api/notificationSettings";
import Logo from "@/assets/logo.svg";
import { AuthGuard } from "@/components/AuthGuard";
import { Button } from "@/components/ui/button";
import { useCompleteOnboardingMutation, useGenerateExamMutation } from "@/hooks/queries";
import { trackEvent } from "@/lib/analytics";
import {
  DEFAULT_NOTIFICATION_SETTINGS,
  EXAM_QUESTION_COUNT_OPTIONS,
  type ExamQuestionCountOption,
} from "@/lib/constant";
import { isNativeApp } from "@/lib/native";
import { syncNotificationsToNative } from "@/lib/notificationBridge";
import { useAuthStore } from "@/store/authStore";
import { useSettingsStore } from "@/store/settingsStore";

type OnboardingStep = "welcome" | "goal" | "notification" | "start";
type FirstAction = "test" | "flashcards" | "later";

const STARTER_TEST_QUESTION_COUNT = 20;

const GOAL_COPY: Record<ExamQuestionCountOption, { title: string; description: string }> = {
  100: { title: "가볍게 시작", description: "매일 부담 없이" },
  300: { title: "충분히 연습", description: "학습량을 차근차근" },
  500: { title: "집중 학습", description: "한 번에 깊이 있게" },
};

function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function OnboardingPage() {
  const { replace } = useNavigate();
  const userId = useAuthStore(state => state.user?.id);
  const questionCount = useSettingsStore(state => state.examQuestionCount);
  const setQuestionCount = useSettingsStore(state => state.setExamQuestionCount);
  const completeOnboarding = useCompleteOnboardingMutation();
  const generateExam = useGenerateExamMutation();
  const showNotificationStep =
    isNativeApp ||
    (import.meta.env.DEV &&
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).has("native-preview"));

  const steps = useMemo<OnboardingStep[]>(
    () => (showNotificationStep ? ["welcome", "goal", "notification", "start"] : ["welcome", "goal", "start"]),
    [showNotificationStep],
  );
  const [stepIndex, setStepIndex] = useState(0);
  const [notificationGranted, setNotificationGranted] = useState(false);
  const [isRequestingNotification, setIsRequestingNotification] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const startedTracked = useRef(false);

  const step = steps[stepIndex];
  const progress = ((stepIndex + 1) / steps.length) * 100;

  useEffect(() => {
    if (startedTracked.current) {
      return;
    }

    startedTracked.current = true;
    void trackEvent("onboarding_started", { source: "onboarding", progress: Math.round(progress) });
  }, [progress]);

  useEffect(() => {
    void trackEvent("onboarding_step_viewed", {
      source: "onboarding",
      progress: Math.round(progress),
      step,
    });
  }, [progress, step]);

  useEffect(() => {
    if (step === "start") {
      void trackEvent("onboarding_starter_test_impression", {
        source: "onboarding",
        progress: 100,
        question_count: STARTER_TEST_QUESTION_COUNT,
        daily_question_count: questionCount,
      });
    }
  }, [questionCount, step]);

  function moveNext(completedStep: OnboardingStep, skipped = false) {
    void trackEvent(skipped ? "onboarding_step_skipped" : "onboarding_step_completed", {
      source: "onboarding",
      progress: Math.round(progress),
      step: completedStep,
    });
    setStepIndex(index => Math.min(index + 1, steps.length - 1));
  }

  async function allowNotifications() {
    if (!userId) {
      return;
    }

    setIsRequestingNotification(true);

    try {
      const current = await getNotificationSettings(userId);
      const settings = { ...DEFAULT_NOTIFICATION_SETTINGS, ...current, enabled: true };

      await upsertNotificationSettings(userId, { enabled: true });
      const status = await syncNotificationsToNative(settings);
      setNotificationGranted(status === "granted");
      void trackEvent("onboarding_notification_choice", {
        source: "onboarding",
        choice: "allow",
        granted: status === "granted",
      });
      moveNext("notification");
    } catch {
      toast.error("알림 설정을 불러오지 못했어요. 설정에서 다시 켤 수 있어요.", {
        position: "top-center",
        richColors: true,
      });
      void trackEvent("onboarding_notification_failed");
      void trackEvent("onboarding_notification_choice", {
        source: "onboarding",
        choice: "allow",
        granted: false,
        failed: true,
      });
      moveNext("notification", true);
    } finally {
      setIsRequestingNotification(false);
    }
  }

  async function skipNotifications() {
    if (userId) {
      try {
        await upsertNotificationSettings(userId, { enabled: false });
      } catch {
        // 온보딩 진행을 막지 않는다. 다음 설정 변경에서 다시 저장할 수 있다.
      }
    }

    setNotificationGranted(false);
    void trackEvent("onboarding_notification_choice", {
      source: "onboarding",
      choice: "later",
      granted: false,
    });
    moveNext("notification", true);
  }

  async function finish(firstAction: FirstAction) {
    if (isFinishing) {
      return;
    }

    setIsFinishing(true);
    void trackEvent("onboarding_step_completed", {
      source: "onboarding",
      progress: 100,
      step: "start",
      first_action: firstAction,
    });

    if (firstAction === "test") {
      void trackEvent("onboarding_starter_test_clicked", {
        source: "onboarding",
        progress: 100,
        question_count: STARTER_TEST_QUESTION_COUNT,
        daily_question_count: questionCount,
      });
    }

    let completionSaved = true;

    try {
      await completeOnboarding.mutateAsync();
    } catch {
      completionSaved = false;
      void trackEvent("onboarding_completion_failed");
    }

    void trackEvent("onboarding_completed", {
      source: "onboarding",
      progress: 100,
      question_count: questionCount,
      starter_question_count: firstAction === "test" ? STARTER_TEST_QUESTION_COUNT : null,
      notification_granted: notificationGranted,
      first_action: firstAction,
      completion_saved: completionSaved,
    });

    if (firstAction === "test") {
      try {
        await generateExam.mutateAsync({
          examDate: toLocalDateString(new Date()),
          questionCount: STARTER_TEST_QUESTION_COUNT,
          entry: "onboarding_starter",
        });
      } catch {
        toast.error("테스트를 만들지 못했어요. 잠시 후 다시 시도해 주세요.", {
          position: "top-center",
          richColors: true,
        });
        replace("/", {}, { transitionName: "none" });
      }
    } else if (firstAction === "flashcards") {
      replace("/flashcards", {}, { transitionName: "none" });
    } else {
      replace("/", {}, { transitionName: "none" });
    }

    setIsFinishing(false);
  }

  return (
    <AuthGuard allowIncompleteOnboarding>
      <Screen>
        <main className="relative flex min-h-[100dvh] overflow-hidden bg-primary-background pt-safe pb-safe">
          <div className="pointer-events-none absolute -top-24 -right-20 size-72 rounded-full bg-primary/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 -left-16 size-80 rounded-full bg-secondary/10 blur-3xl" />

          <section className="relative mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col px-6 py-6 sm:py-10">
            <div className="mb-4 flex shrink-0 items-center gap-3 sm:mb-8">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/70">
                <div
                  role="progressbar"
                  aria-label="온보딩 진행률"
                  aria-valuemin={1}
                  aria-valuemax={steps.length}
                  aria-valuenow={stepIndex + 1}
                  className="h-full rounded-full bg-primary transition-[width] duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-xs font-semibold text-muted-foreground">
                {stepIndex + 1}/{steps.length}
              </span>
            </div>

            <div className="flex min-h-0 flex-1 flex-col">
              {step === "welcome" && (
                <div className="flex min-h-0 flex-1 flex-col">
                  <div className="scrollbar-hidden flex min-h-0 flex-1 items-center justify-center overflow-y-auto py-4 sm:py-6">
                    <div className="flex -translate-y-2 flex-col items-center text-center">
                      <div className="mb-8 flex size-32 items-center justify-center rounded-[2rem] bg-white">
                        <img src={Logo} alt="tobo" className="size-24" />
                      </div>
                      <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                        <SparklesIcon className="size-3.5" />
                        나만의 학습 루틴 만들기
                      </span>
                      <h1 className="text-3xl font-black tracking-tight text-foreground">매일 조금씩, 확실하게</h1>
                      <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                        오늘부터 부담 없는 목표로
                        <br />
                        새로운 단어를 내 것으로 만들어 보세요.
                      </p>
                    </div>
                  </div>

                  <div className="shrink-0 pt-4">
                    <Button
                      className="relative h-13 w-full px-12 text-base font-bold"
                      onClick={() => moveNext("welcome")}
                    >
                      시작하기
                      <ChevronRightIcon aria-hidden className="absolute right-5 size-4" />
                    </Button>
                  </div>
                </div>
              )}

              {step === "goal" && (
                <div className="flex min-h-0 flex-1 flex-col">
                  <header className="shrink-0 pt-2">
                    <div className="mb-4 flex size-12 items-center justify-center rounded-2xl bg-primary/10">
                      <TargetIcon className="size-6 text-primary" />
                    </div>
                    <h1 className="text-2xl font-black tracking-tight text-foreground">하루에 몇 문제씩 풀까요?</h1>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      지금 고른 목표는 설정에서 언제든 바꿀 수 있어요.
                    </p>
                  </header>

                  <div className="scrollbar-hidden min-h-0 flex-1 overflow-y-auto">
                    <div className="flex min-h-full items-center py-4">
                      <div className="grid w-full gap-3">
                        {EXAM_QUESTION_COUNT_OPTIONS.map(count => {
                          const selected = questionCount === count;

                          return (
                            <button
                              key={count}
                              type="button"
                              aria-pressed={selected}
                              onClick={() => setQuestionCount(count)}
                              className={`flex w-full items-center gap-4 rounded-2xl border-2 bg-white px-5 py-4 text-left transition-[border-color,transform] active:scale-[0.99] ${
                                selected ? "border-primary" : "border-transparent"
                              }`}
                            >
                              <div
                                className={`flex size-11 shrink-0 items-center justify-center rounded-xl text-sm font-black ${
                                  selected ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                                }`}
                              >
                                {count}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-bold text-foreground">{GOAL_COPY[count].title}</p>
                                <p className="text-xs text-muted-foreground">{GOAL_COPY[count].description}</p>
                              </div>
                              <div
                                className={`flex size-6 items-center justify-center rounded-full border ${
                                  selected ? "border-primary bg-primary text-white" : "border-border text-transparent"
                                }`}
                              >
                                <CheckIcon className="size-3.5" />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="shrink-0 pt-4">
                    <Button
                      className="h-13 w-full text-base font-bold"
                      onClick={() => {
                        void trackEvent("onboarding_goal_selected", {
                          source: "onboarding",
                          question_count: questionCount,
                        });
                        moveNext("goal");
                      }}
                    >
                      {questionCount}문제로 시작할게요
                    </Button>
                  </div>
                </div>
              )}

              {step === "notification" && (
                <div className="flex min-h-0 flex-1 flex-col text-center">
                  <div className="scrollbar-hidden flex min-h-0 flex-1 items-center justify-center overflow-y-auto py-6">
                    <div className="flex flex-col items-center">
                      <div className="relative mb-7 flex size-28 items-center justify-center rounded-[2rem] bg-white">
                        <BellRingIcon className="size-12 text-primary" />
                        <span className="absolute -right-1 -top-1 flex size-8 items-center justify-center rounded-full bg-secondary text-sm font-black text-white">
                          1
                        </span>
                      </div>
                      <h1 className="text-2xl font-black tracking-tight text-foreground">학습할 때 알려드릴까요?</h1>
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                        매일 정한 시간과 멈춘 학습을 알려드려요.
                        <br />
                        원하지 않으면 언제든 설정에서 끌 수 있어요.
                      </p>
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-col gap-3 pt-4">
                    <Button
                      className="h-13 w-full gap-2 text-base font-bold"
                      disabled={isRequestingNotification}
                      onClick={allowNotifications}
                    >
                      {isRequestingNotification ? (
                        <LoaderCircleIcon className="size-4 animate-spin" />
                      ) : (
                        <BellIcon className="size-4" />
                      )}
                      알림 허용하기
                    </Button>
                    <Button
                      variant="ghost"
                      className="h-11 w-full text-muted-foreground"
                      disabled={isRequestingNotification}
                      onClick={skipNotifications}
                    >
                      나중에 할게요
                    </Button>
                  </div>
                </div>
              )}

              {step === "start" && (
                <div className="flex min-h-0 flex-1 flex-col">
                  <div className="scrollbar-hidden flex min-h-0 flex-1 items-center justify-center overflow-y-auto py-6">
                    <div className="text-center">
                      <div className="mx-auto mb-6 flex size-24 items-center justify-center rounded-[2rem] bg-white">
                        <CheckIcon className="size-11 text-primary" strokeWidth={3} />
                      </div>
                      <h1 className="text-2xl font-black tracking-tight text-foreground">준비가 끝났어요!</h1>
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                        오늘의 첫 학습을 바로 시작해 볼까요?
                      </p>
                    </div>
                  </div>

                  <div className="shrink-0 pt-4">
                    <div className="grid gap-3">
                      <button
                        type="button"
                        disabled={isFinishing}
                        onClick={() => finish("test")}
                        className="flex w-full items-center gap-4 rounded-2xl bg-primary px-5 py-4 text-left text-primary-foreground transition-transform active:scale-[0.99] disabled:opacity-60"
                      >
                        <div className="flex size-11 items-center justify-center rounded-xl bg-white/15">
                          {isFinishing ? (
                            <LoaderCircleIcon className="size-5 animate-spin" />
                          ) : (
                            <ClipboardPenIcon className="size-5" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-bold">20문제 스타터 테스트</p>
                          <p className="text-xs text-primary-foreground/75">
                            첫 테스트만 가볍게 · 이후 하루 {questionCount}문제
                          </p>
                        </div>
                        <ChevronRightIcon className="size-4" />
                      </button>

                      <button
                        type="button"
                        disabled={isFinishing}
                        onClick={() => finish("flashcards")}
                        className="flex w-full items-center gap-4 rounded-2xl bg-white px-5 py-4 text-left transition-transform active:scale-[0.99] disabled:opacity-60"
                      >
                        <div className="flex size-11 items-center justify-center rounded-xl bg-secondary/10">
                          <BookOpenIcon className="size-5 text-secondary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-foreground">플래시카드 둘러보기</p>
                          <p className="text-xs text-muted-foreground">카드를 넘기며 가볍게 학습</p>
                        </div>
                        <ChevronRightIcon className="size-4 text-muted-foreground" />
                      </button>
                    </div>

                    <Button
                      variant="ghost"
                      className="mt-3 h-11 w-full text-muted-foreground"
                      disabled={isFinishing}
                      onClick={() => finish("later")}
                    >
                      나중에 할게요
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </section>
        </main>
      </Screen>
    </AuthGuard>
  );
}
